import { motion } from 'framer-motion';
import { FaEnvelope, FaPhone, FaWhatsapp } from 'react-icons/fa';
import { CONTACT } from '../../utils/constants';
import { scaleIn } from '../../utils/animations';

const TeamPointer = () => {
  return (
    <div className="container mx-auto px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-5xl mx-auto"
      >
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-primary-green-dark mb-4">
            Meet Our Team 👥
          </h2>
          <p className="text-lg md:text-xl text-neutral-gray-dark max-w-2xl mx-auto">
            Passionate educators, creative writers, and design wizards working together!
          </p>
        </div>

        {/* Team Description */}
        <motion.div
          variants={scaleIn}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="card bg-white mb-12"
        >
          <div className="flex items-start gap-6 mb-6">
            <div className="text-6xl">🎨</div>
            <div className="flex-1">
              <h3 className="text-2xl font-display font-bold text-primary-green-dark mb-3">
                Who We Are
              </h3>
              <p className="text-lg text-neutral-gray-dark leading-relaxed mb-4">
                Our team consists of experienced <strong className="text-primary-green">educators, 
                creative writers, illustrators, and child development experts</strong> who are 
                passionate about making learning enjoyable for children.
              </p>
              <p className="text-lg text-neutral-gray-dark leading-relaxed">
                Every issue of The Tiny Times is carefully crafted with love, ensuring that each 
                story, puzzle, and activity is both <em className="text-accent-orange font-semibold">educational and entertaining</em>!
              </p>
            </div>
          </div>

          {/* Team Values */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            {teamValues.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 bg-accent-blue/10 rounded-lg p-4"
              >
                <span className="text-3xl">{value.icon}</span>
                <div>
                  <div className="font-display font-bold text-primary-green-dark">
                    {value.title}
                  </div>
                  <div className="text-sm text-neutral-gray-dark">
                    {value.description}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Contact the Team */}
        <motion.div
          variants={scaleIn}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="rounded-[2.5rem] p-8 md:p-12 text-center relative overflow-hidden border border-white/60 mb-12"
          style={{
            background: 'rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.08)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-green-400/10 via-blue-400/10 to-purple-400/10 pointer-events-none"></div>
          
          <div className="text-center mb-10 relative z-10">
            <div className="text-6xl mb-4">📞</div>
            <h3 className="text-3xl md:text-5xl font-display font-bold mb-4" style={{ color: '#1A1F2E' }}>
              Want to Get in Touch?
            </h3>
            <p className="text-lg md:text-xl text-neutral-gray-dark max-w-2xl mx-auto">
              We'd love to hear from you! Whether you have questions, feedback, or 
              just want to say hello, our team is here for you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto mb-10 relative z-10">
            {/* WhatsApp (Now Primary) */}
            <motion.a
              href={CONTACT.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.04, y: -4 }}
              whileTap={{ scale: 0.97 }}
              className="flex flex-col items-center gap-2 p-5 rounded-2xl text-white transition-all order-1"
              style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)', boxShadow: '0 8px 32px rgba(37,211,102,0.3)' }}
            >
              <FaWhatsapp className="text-4xl" />
              <div className="font-display font-bold">WhatsApp</div>
              <div className="text-xs text-white/90">Instant Response</div>
            </motion.a>

            {/* Phone */}
            <motion.a
              href={`tel:${CONTACT.phone}`}
              whileHover={{ scale: 1.04, y: -4 }}
              whileTap={{ scale: 0.97 }}
              className="flex flex-col items-center gap-2 p-5 rounded-2xl transition-all order-2"
              style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.9)', color: '#1A1F2E' }}
            >
              <FaPhone className="text-4xl" style={{ color: '#1565C0' }} />
              <div className="font-display font-bold">Call Us</div>
              <div className="text-xs text-neutral-gray-dark">{CONTACT.phone}</div>
            </motion.a>

            {/* Email - Fixed Overflow */}
            <motion.a
              href={`mailto:${CONTACT.email}?subject=Hello from The Tiny Times`}
              whileHover={{ scale: 1.04, y: -4 }}
              whileTap={{ scale: 0.97 }}
              className="flex flex-col items-center justify-center gap-2 p-5 rounded-2xl transition-all order-3"
              style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.9)', color: '#1A1F2E' }}
            >
              <FaEnvelope className="text-4xl" style={{ color: '#F57C00' }} />
              <div className="font-display font-bold">Email Us</div>
              <div className="text-xs text-neutral-gray-dark truncate w-full max-w-[220px]" title={CONTACT.email}>
                {CONTACT.email}
              </div>
            </motion.a>
          </div>

          <div className="text-center relative z-10">
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center justify-center bg-white text-primary-green-dark px-10 py-4 rounded-button font-display font-bold text-lg shadow-[0_8px_24px_rgba(0,0,0,0.1)] hover:shadow-[0_12px_28px_rgba(0,0,0,0.15)] transition-all"
            >
              Visit Contact Page →
            </motion.a>
          </div>
        </motion.div>

        {/* Join Our Team */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-12 text-center"
        >
          <div className="card bg-gradient-to-r from-accent-yellow/20 to-accent-orange/20 border-2 border-accent-orange/30">
            <div className="text-5xl mb-4">🌟</div>
            <h3 className="text-2xl md:text-3xl font-display font-bold text-primary-green-dark mb-3">
              Want to Join Our Team?
            </h3>
            <p className="text-lg text-neutral-gray-dark mb-6 max-w-2xl mx-auto">
              We're always looking for talented educators, writers, and creative minds 
              who share our passion for children's education!
            </p>
            <a
              href={`mailto:${CONTACT.email}?subject=Career Opportunity at The Tiny Times`}
              className="inline-block bg-accent-orange text-white px-6 py-3 rounded-button font-display font-semibold text-lg hover:scale-105 transition-transform"
            >
              Send Your Resume 📧
            </a>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

// Team values data
const teamValues = [
  {
    icon: '💖',
    title: 'Passionate',
    description: 'We love what we do',
  },
  {
    icon: '🎓',
    title: 'Experienced',
    description: 'Education experts',
  },
  {
    icon: '🤗',
    title: 'Caring',
    description: 'Kids come first',
  },
];

export default TeamPointer;